import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from '@ld.consumer-portal/i18n';
import {
  navCalculatorsIcon,
  navCalculatorsIconSelected,
  navHomeIcon,
  navHomeIconSelected,
  navProductsIcon,
  navProductsIconSelected,
} from '@ld.consumer-portal/data-uri';
import { MobileNavItem } from '@ld.consumer-portal/models';
import { MobileNavService } from '@ld.consumer-portal/routing';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

const DEFAULT_NAV_ITEMS: MobileNavItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: navHomeIcon,
    iconSelected: navHomeIconSelected,
    route: '/',
  },
  {
    id: 'products',
    label: 'Products',
    icon: navProductsIcon,
    iconSelected: navProductsIconSelected,
    route: '/products',
  },
  {
    id: 'calculators',
    label: 'Calculators',
    icon: navCalculatorsIcon,
    iconSelected: navCalculatorsIconSelected,
    route: '/calculators',
  },
];

@Component({
  selector: 'ld-mobile-bottom-nav',
  imports: [SvgIconComponent, TranslocoDirective],
  templateUrl: './mobile-bottom-nav.component.html',
  styleUrl: './mobile-bottom-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileBottomNavComponent {
  private readonly mobileNavService = inject(MobileNavService);
  private readonly translationService = inject(TranslationService);
  private readonly translocoService = inject(TranslocoService);

  navItems = input<MobileNavItem[]>(DEFAULT_NAV_ITEMS);

  protected readonly activeItemId = computed(() => this.mobileNavService.activeTab());

  protected readonly translatedNavItems = computed<MobileNavItem[]>(() => {
    this.translationService.currentLanguage();
    return this.navItems().map((item) => {
      const labelKey = this.getNavItemLabelKey(item.id);
      return labelKey ? { ...item, label: this.translocoService.translate(labelKey) } : item;
    });
  });

  private getNavItemLabelKey(id: string): string | null {
    switch (id) {
      case 'home':
        return 'mobileNavHome';
      case 'products':
        return 'mobileNavProducts';
      case 'calculators':
        return 'mobileNavCalculators';
      default:
        return null;
    }
  }

  navItemClick = output<MobileNavItem>();

  protected getIconSrc(item: MobileNavItem): string {
    return item.id === this.activeItemId() ? item.iconSelected : item.icon;
  }

  protected isActive(item: MobileNavItem): boolean {
    return item.id === this.activeItemId();
  }

  protected onNavItemClick(item: MobileNavItem): void {
    this.navItemClick.emit(item);
  }
}
